import { useState } from "react";
import PageSEO from "@/components/PageSEO";
import StickyHeader from "@/components/StickyHeader";
import Breadcrumbs from "@/components/Breadcrumbs";
import SiteFooter from "@/components/SiteFooter";
import { Check, Phone, Shield, Clock, Star, FileText } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

const ScheduleQuote = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    fullName: "",
    phone: "",
    email: "",
    postCode: "",
    systemType: "",
    propertyType: "",
    preferredDate: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setTimeout(() => {
      toast({ title: "Quote Request Submitted!", description: "We'll get back to you with a free quote as soon as possible." });
      setIsSubmitting(false);
      setFormData({ fullName: "", phone: "", email: "", postCode: "", systemType: "", propertyType: "", preferredDate: "", message: "" });
    }, 1500);
  };

  return (
    <div className="min-h-screen bg-background">
      <PageSEO title="Get a Free AC Quote | No Obligation | ACG Southern Highlands" description="Request a free, no-obligation air conditioning quote online. Our team will visit your property, assess your needs and provide transparent pricing." />
      <StickyHeader />
      <Breadcrumbs items={[{ label: "Schedule Quote" }]} />

      {/* Hero Banner — Orange theme */}
      <section className="relative overflow-hidden">
        <img
          src="https://airconditioningguys.com.au/uploads/common_images/service_tmp_repair_008.jpg"
          alt="Schedule a free air conditioning quote"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black/70" />
        <div className="relative container mx-auto px-4 py-20 md:py-28">
          <div className="max-w-2xl">
            <div className="w-10 h-10 border-l-4 border-t-4 border-white/60 mb-6" />
            <h1 className="text-4xl md:text-6xl font-black text-primary-foreground mb-4">
              Schedule Quote
            </h1>
            <p className="text-xl md:text-2xl font-semibold text-primary-foreground/90 mb-6">
              Receive Your Free Onsite Quotation
            </p>
            <ul className="space-y-3">
              <li className="flex items-center gap-3 text-primary-foreground">
                <Check className="w-5 h-5 text-green-300 flex-shrink-0" />
                <span className="font-medium">Same Day Installations</span>
              </li>
              <li className="flex items-center gap-3 text-primary-foreground">
                <Check className="w-5 h-5 text-green-300 flex-shrink-0" />
                <span className="font-medium">Servicing the Southern Highlands</span>
              </li>
              <li className="flex items-center gap-3 text-primary-foreground">
                <Check className="w-5 h-5 text-green-300 flex-shrink-0" />
                <span className="font-medium">No Obligation, 100% Free Quotes</span>
              </li>
            </ul>
          </div>
        </div>
      </section>

      {/* Quote Form Section */}
      <section className="py-16 bg-muted">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-3 gap-12 max-w-6xl mx-auto">
            {/* Form */}
            <div className="lg:col-span-2">
              <div className="bg-card rounded-2xl shadow-xl p-8 md:p-10 border border-border">
                <div className="flex items-center gap-3 mb-2">
                  <img
                    src="https://airconditioningguys.com.au/themes/acg/images/service_quoteac.png"
                    alt="AC unit"
                    className="w-16 h-16 object-contain"
                  />
                  <div>
                    <h2 className="text-2xl font-bold text-foreground">Schedule Your Quote</h2>
                    <p className="text-muted-foreground text-sm">
                      Simply complete our form below & we'll get back to you as soon as possible
                    </p>
                  </div>
                </div>

                <div className="border-t border-border my-6" />

                <h3 className="text-lg font-bold text-foreground mb-6">Your Contact Information</h3>

                <form onSubmit={handleSubmit} className="space-y-5">
                  <div className="grid md:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-1.5">Full Name *</label>
                      <input
                        type="text"
                        name="fullName"
                        value={formData.fullName}
                        onChange={handleChange}
                        required
                        placeholder="Please provide your full name"
                        className="w-full px-4 py-3 rounded-lg border border-border bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-colors"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-1.5">Contact Number *</label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        required
                        placeholder="Please provide your contact number"
                        className="w-full px-4 py-3 rounded-lg border border-border bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-colors"
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-1.5">Email Address *</label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        placeholder="What is your email address?"
                        className="w-full px-4 py-3 rounded-lg border border-border bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-colors"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-1.5">Post Code *</label>
                      <input
                        type="text"
                        name="postCode"
                        value={formData.postCode}
                        onChange={handleChange}
                        required
                        placeholder="Please provide your post code"
                        className="w-full px-4 py-3 rounded-lg border border-border bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-colors"
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-1.5">System Type</label>
                      <select
                        name="systemType"
                        value={formData.systemType}
                        onChange={handleChange}
                        className="w-full px-4 py-3 rounded-lg border border-border bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-colors"
                      >
                        <option value="">Please select system type</option>
                        <option value="split">Split System</option>
                        <option value="ducted">Ducted System</option>
                        <option value="multi-head">Multi-Head Split</option>
                        <option value="cassette">Cassette System</option>
                        <option value="other">Other / Not Sure</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-1.5">Property Type</label>
                      <select
                        name="propertyType"
                        value={formData.propertyType}
                        onChange={handleChange}
                        className="w-full px-4 py-3 rounded-lg border border-border bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-colors"
                      >
                        <option value="">Please select property type</option>
                        <option value="residential">Residential</option>
                        <option value="commercial">Commercial</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1.5">Preferred Date</label>
                    <input
                      type="date"
                      name="preferredDate"
                      value={formData.preferredDate}
                      onChange={handleChange}
                      className="w-full px-4 py-3 rounded-lg border border-border bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-colors"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1.5">What Do You Need a Quote For?</label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      rows={4}
                      placeholder="Tell us about the installation or system you'd like quoted..."
                      className="w-full px-4 py-3 rounded-lg border border-border bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-colors resize-none"
                    />
                  </div>

                  {/* Privacy Notice */}
                  <div className="flex items-center gap-2 text-xs text-muted-foreground bg-muted rounded-lg px-4 py-3">
                    <Shield className="w-4 h-4 text-secondary flex-shrink-0" />
                    <span><strong>Your privacy is our priority.</strong> All information will be kept 100% safe & secure.</span>
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-primary hover:bg-primary/90 text-primary-foreground font-bold uppercase text-lg py-4 rounded-lg transition-colors disabled:opacity-60"
                  >
                    {isSubmitting ? "Submitting..." : "Submit Quote Request"}
                  </button>
                </form>
              </div>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Quick Contact */}
              <div className="bg-primary rounded-2xl p-6 text-primary-foreground">
                <h3 className="font-bold text-lg mb-3">Prefer to Talk?</h3>
                <p className="text-primary-foreground/80 text-sm mb-4">Call us now for a free phone estimate or to book your onsite quote.</p>
                <a
                  href="tel:0480841485"
                  className="flex items-center justify-center gap-2 bg-primary-foreground text-primary font-bold py-3 px-6 rounded-lg hover:opacity-90 transition-opacity w-full"
                >
                  <Phone className="w-5 h-5" />
                  0480 841 485
                </a>
              </div>

              {/* What's Included */}
              <div className="bg-card rounded-2xl p-6 border border-border shadow-sm">
                <h3 className="font-bold text-foreground mb-4">Your Free Quote Includes</h3>
                <ul className="space-y-4">
                  {[
                    { icon: FileText, title: "Detailed Written Quote", text: "Itemised pricing with no hidden costs" },
                    { icon: Clock, title: "Onsite Assessment", text: "Professional evaluation of your space" },
                    { icon: Star, title: "System Recommendations", text: "Expert advice on the best system for you" },
                    { icon: Shield, title: "No Obligation", text: "Zero pressure — take your time to decide" },
                  ].map((item) => (
                    <li key={item.title} className="flex gap-3">
                      <div className="w-9 h-9 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                        <item.icon className="w-4 h-4 text-primary" />
                      </div>
                      <div>
                        <p className="font-semibold text-sm text-foreground">{item.title}</p>
                        <p className="text-xs text-muted-foreground">{item.text}</p>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Review Badges */}
              <div className="bg-card rounded-2xl p-6 border border-border shadow-sm">
                <h3 className="font-bold text-foreground mb-4">Customer Reviews</h3>
                <div className="space-y-3">
                  {[
                    { platform: "Google", reviews: "490+", rating: "4.9" },
                    { platform: "ProductReview", reviews: "450+", rating: "4.8" },
                    { platform: "Facebook", reviews: "43+", rating: "5.0" },
                  ].map((r) => (
                    <div key={r.platform} className="flex items-center justify-between bg-muted rounded-lg px-4 py-3">
                      <span className="text-sm font-medium text-foreground">{r.platform}</span>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-muted-foreground">{r.reviews} reviews</span>
                        <span className="bg-primary/10 text-primary font-bold text-sm px-2 py-0.5 rounded">
                          ★ {r.rating}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
};

export default ScheduleQuote;
